import type { SolidTemplateEntry } from './load-template-entry'
import type { SolidMountResult } from './mount-solid-template.types'
import type { SolidDataState } from '@app/application/solid/services/solid-data-loader'
import type {
  SolidRequestContext,
  SolidTemplateBoot,
} from '@app/shared/types/sf-data'

import { loadStylesheets } from './load-stylesheets'
import { loadTemplateEntry } from './load-template-entry'
import { SOLID_TEMPLATE_READY_TIMEOUT } from './mount-solid-template.constants'
import { SfLog } from '@app/adapters/logger'

// Loads the period's data, mounts the template into el once it is ready, and
// logs a failure. With refreshData, a period the loader holds is refreshed
// too. A screen that shows many templates sends that refresh once itself.
// An abort of signal before the mount resolves 'aborted' and logs nothing,
// and an abort after it disposes the template.
export const mountSolidTemplate = async ({
  boot,
  el,
  signal,
  refreshData = true,
}: {
  boot: SolidTemplateBoot
  el: HTMLElement
  signal: AbortSignal
  refreshData?: boolean
}): Promise<SolidMountResult> => {
  try {
    const entry = await waitForSolidTemplate({ boot, signal, refreshData })

    if (signal.aborted) {
      return 'aborted'
    }

    const dispose = entry.mount(el, boot.requestContext)

    signal.addEventListener('abort', dispose, { once: true })

    return 'mounted'
  } catch (error) {
    if (signal.aborted) {
      return 'aborted'
    }

    SfLog.error('[solid] failed to boot template entry', {
      error: error as Error,
      extra: {
        entryUrl: boot.entryUrl,
        companyId: boot.requestContext.companyId,
      },
    })

    return 'failed'
  }
}

// Resolves with the entry once it loads. The stylesheets and the period's data
// hold the reveal back for SOLID_TEMPLATE_READY_TIMEOUT at most, then arrive
// late. A failed entry, or a data failure inside the timeout, rejects. After an
// abort of signal, nothing is logged for a page that is gone.
const waitForSolidTemplate = async ({
  boot: { entryUrl, stylesheetUrls, requestContext },
  signal,
  refreshData,
}: {
  boot: SolidTemplateBoot
  signal: AbortSignal
  refreshData: boolean
}): Promise<SolidTemplateEntry> => {
  let assetsReady = false

  const assets = Promise.all([
    loadStylesheets(stylesheetUrls),
    loadSolidData({ requestContext, refreshData }),
  ]).then(() => {
    assetsReady = true
  })

  // Past the timeout, a late data failure changes nothing on screen.
  assets.catch(() => undefined)

  let timer: ReturnType<typeof setTimeout> | undefined

  const timedOut = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, SOLID_TEMPLATE_READY_TIMEOUT)
  })

  try {
    const [entry] = await Promise.all([
      loadTemplateEntry(entryUrl),
      Promise.race([assets, timedOut]),
    ])

    if (!assetsReady && !signal.aborted) {
      SfLog.warn('[solid] template revealed before its styles or data', {
        extra: { entryUrl },
      })
    }

    return entry
  } finally {
    clearTimeout(timer)
  }
}

// The loader refreshes a held period only when it gets a reconciliation text
// id. Held data stays readable while a refresh runs, so only a period the
// loader does not hold yet waits for the response.
const loadSolidData = async ({
  requestContext: { firmId, companyId, ledgerId, reconciliationTextId },
  refreshData,
}: {
  requestContext: SolidRequestContext
  refreshData: boolean
}): Promise<void> => {
  const loader = window.silverfinSolidData

  if (!loader) {
    return
  }

  const refresh = loader.loadData(
    firmId,
    companyId,
    ledgerId,
    refreshData ? reconciliationTextId : undefined
  )

  if (holdsPeriod({ state: loader.get(firmId, companyId), ledgerId })) {
    return
  }

  await refresh

  const state = loader.get(firmId, companyId)

  if (state.status === 'error' && !holdsPeriod({ state, ledgerId })) {
    throw new Error('Solid data failed to load', { cause: state.error })
  }
}

const holdsPeriod = ({
  state: { data },
  ledgerId,
}: {
  state: SolidDataState
  ledgerId: number
}): boolean => Boolean(data && String(ledgerId) in data.periods)
